import { useState, type ReactNode } from 'react';
import { useMyAttendance } from '@/features/attendance/api';
import { useMyLocumContract } from '@/features/locum/api';
import { useMySchedule, type StaffSchedule } from '@/features/schedules/api';
import { todayKl } from '@/pages/calendar/time';

type TileValue = { value: ReactNode; caption?: ReactNode };

const DAY_MS = 86_400_000;
const addDays = (ymd: string, days: number) =>
  new Date(Date.parse(`${ymd}T00:00:00Z`) + days * DAY_MS)
    .toISOString()
    .slice(0, 10);
const hhmm = (min: number) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const dayLabel = (ymd: string) =>
  new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${ymd}T00:00:00Z`));

/**
 * RL-1808 (gap #75): the locum's next shift from their weekly hours and date
 * overrides (an override replaces the weekly hours that day, as on the API),
 * looking two weeks ahead. Today counts while the shift has not ended.
 */
export function nextShift(
  rows: StaffSchedule[],
  today: string,
  nowMin: number,
): { date: string; startMin: number; endMin: number } | null {
  for (let i = 0; i < 14; i++) {
    const date = addDays(today, i);
    const overrides = rows.filter(
      (r) => r.kind === 'date_override' && r.date === date,
    );
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    const shifts = (
      overrides.length
        ? overrides
        : rows.filter(
            (r) => r.kind === 'weekly_template' && r.weekday === weekday,
          )
    )
      .filter((r) => r.endMin > r.startMin && (i > 0 || r.endMin > nowMin))
      .sort((x, y) => x.startMin - y.startMin);
    if (shifts.length)
      return { date, startMin: shifts[0].startMin, endMin: shifts[0].endMin };
  }
  return null;
}

/** Live values for the locum's My next shift, Contract ends and Attendance tiles. */
export function useLocumTiles(enabled: boolean): Record<string, TileValue> {
  // Review fix: only the locum's dashboard asks for these.
  const schedule = useMySchedule(enabled);
  const contract = useMyLocumContract(enabled);
  const attendance = useMyAttendance(enabled);
  // The clinic day and minute when the dashboard opened.
  const [{ today, nowMin }] = useState(() => {
    const kl = new Date(Date.now() + 8 * 3_600_000);
    return {
      today: todayKl(),
      nowMin: kl.getUTCHours() * 60 + kl.getUTCMinutes(),
    };
  });
  if (!enabled) return {};
  const tiles: Record<string, TileValue> = {};
  if (schedule.data) {
    const next = nextShift(
      [...schedule.data.templates, ...schedule.data.overrides],
      today,
      nowMin,
    );
    tiles['My next shift'] = next
      ? {
          value: next.date === today ? 'Today' : dayLabel(next.date),
          caption: `${hhmm(next.startMin)}–${hhmm(next.endMin)}`,
        }
      : { value: 'None', caption: 'No shift in the next 2 weeks' };
  }
  if (contract.data !== undefined)
    tiles['Contract ends'] = contract.data
      ? {
          value: dayLabel(contract.data.endsOn),
          caption: contract.data.branchName,
        }
      : { value: 'None', caption: 'No contract' };
  if (attendance.data) {
    const t = attendance.data.today;
    tiles.Attendance = !t
      ? { value: 'Not clocked in', caption: 'Today' }
      : t.state === 'clocked_in'
        ? { value: 'Clocked in', caption: 'Today' }
        : {
            value: 'Done',
            caption:
              t.workedMinutes === null
                ? 'Today'
                : `${(t.workedMinutes / 60).toFixed(1)} h today`,
          };
  }
  return tiles;
}
